<template>
  <div>
    <slot name="filters-top"></slot>
    <v-card :flat="flat" :tile="tile">
      <v-card-title class="mb-2">
        {{ title ? title : $t('agDatatable.listado') }}
        <slot name="title"></slot>
        <v-spacer></v-spacer>
        <slot name="filters"></slot>
        <v-text-field
          v-if="!hideSearch"
          :placeholder="$t('agDatatable.buscar')"
          v-model="search"
          append-icon="search"
          single-line
          outlined
          dense
          hide-details
          clearable
          @keyup="updateFilter"
          @input="updateFilter"
          class="mt-0 pt-0"></v-text-field>
        <v-btn depressed small dark color="green darken-3" @click="excelExport" class="ml-3">
          <v-icon class="mr-1" size="20">mdi-file-excel</v-icon>
          {{ $t('agDatatable.exportar') }}
        </v-btn>
      </v-card-title>

      <slot></slot>

      <div style="height: 100%">
        <div class="ag-container">
          <ag-grid-vue
            style="width: 100%"
            :style="{ height: height + 'vh' }"
            :suppressCellSelection="true"
            class="ag-theme-material ag-grid"
            id="ag-grid"
            :columnDefs="headers"
            :rowData="items"
            :sideBar="sideBar"
            :autoGroupColumnDef="autoGroupColumnDef"
            :groupSelectsChildren="groupSelectsChildren"
            :animateRows="true"
            :localeText="localeText"
            :pagination="true"
            :paginationAutoPageSize="true"
            :quickFilter="search"
            @grid-ready="onGridReady"
            @rowClicked="clickRow"
            :rowSelection="rowSelection"
            :suppressRowClickSelection="suppressRowClickSelection"
            :masterDetail="!!detailCellRenderer"
            :detailRowHeight="detailRowHeight"
            :detailCellRenderer="detailCellRenderer"
            :detailCellRendererParams="detailCellRendererParams"
            :rowGroupPanelShow="rowGroupPanelShow"
            :defaultColDef="mergedDefaultColDef"
            :overlayLoadingTemplate="overlayLoadingTemplate"
            :frameworkComponents="frameworkComponents"
            :excelStyles="excelStyles"
            @selectionChanged="onSelectionChange()"
            @firstDataRendered="autoSize">
          </ag-grid-vue>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
export default {
  name: 'AgDatatable',
  props: {
    detailRowHeight: { type: Number, default: null },
    detailCellRenderer: { type: String, default: null },
    detailCellRendererParams: null,
    title: { type: String, default: null },
    height: { type: String, default: '72.5' },
    tile: { type: Boolean, default: true },
    flat: { type: Boolean, default: true },
    groupSelectsChildren: { type: Boolean, default: true },
    defaultColDef: {
      type: Object,
      default: () => {
        return {
          editable: true,
          filter: true,
          enableCellChangeFlash: true,
          minWidth: 120,
        }
      },
    },
    rowGroupPanelShow: { type: String, default: 'never' },
    showHover: { type: Boolean, default: true },
    hideOverlay: { type: Boolean, default: false },
    hideSearch: { type: Boolean, default: false },
    showSelect: { type: Boolean, default: false },
    rowSelection: { type: String, default: null },
    sizeColumnsToFit: { type: Boolean, default: false },
    disableClickRow: { type: Boolean, default: false },
    emitClickRow: { type: Boolean, default: false },
    suppressRowClickSelection: { type: Boolean, default: false },
    autoGroupColumnDef: {
      type: Object,
      default: () => ({ resizable: true }),
    },
    headers: { type: Array, default: () => [] },
    items: { type: Array, required: true, default: () => [] },
    is_core: { type: Boolean, default: false },
  },
  mounted() {
    setTimeout(() => (this.loading = false), 1500)
    if (!this.showHover) this.num_hover = 3
  },
  beforeMount() {
    this.overlayLoadingTemplate = `<span class="ag-overlay-loading-center">${this.$t(
      'tabla.overlayLoading'
    )}</span>`
    this.localeText = {
      equals: this.$t('tabla.equals'),
      notEqual: this.$t('tabla.notEqual'),
      lessThan: this.$t('tabla.lessThan'),
      greaterThan: this.$t('tabla.greaterThan'),
      inRange: this.$t('tabla.inRange'),
      contains: this.$t('tabla.contains'),
      noRowsToShow: this.$t('tabla.sindatos'),
      filters: this.$t('tabla.filters'),
      columns: this.$t('tabla.columns'),
    }
    if (this.is_core) {
      this.frameworkComponents = {
        SwitchRenderer: 'SwitchRenderer',
        DeleteRenderer: 'DeleteRenderer',
        BooleanCellEditor: 'BooleanCellEditor',
        AutocompleteCellEditor: 'AutocompleteCellEditor',
        SelectCellEditor: 'SelectCellEditor',
        //TextCellEditor: 'TextCellEditor',
        DateCellEditor: 'DateCellEditor',
        AccionCorrectivaRenderer: 'AccionCorrectivaRenderer',
        //TipoEstaticoCellEditor: 'TipoEstaticoCellEditor',
      }
    } else {
      this.frameworkComponents = {
        SwitchRenderer: 'SwitchRenderer',
        DeleteRenderer: 'DeleteRenderer',
        BooleanCellEditor: 'BooleanCellEditor',
        AutocompleteCellEditor: 'AutocompleteCellEditor',
        SelectCellEditor: 'SelectCellEditor',
        TextCellEditor: 'TextCellEditor',
        DateCellEditor: 'DateCellEditor',
        AccionCorrectivaRenderer: 'AccionCorrectivaRenderer',
        TipoEstaticoCellEditor: 'TipoEstaticoCellEditor',
      }
    }
    this.sideBar = {
      toolPanels: ['filters', 'columns'],
    }
    this.excelStyles = [
      {
        id: 'default',
        dataType: 'string',
      },
      {
        id: 'stringType',
        dataType: 'string',
      },
      {
        id: 'string',
        dataType: 'string',
      },
    ]
  },
  data() {
    return {
      file: undefined,
      singleSelect: false,
      loading: false,
      search: '',
      page: 1,
      pageCount: 0,
      localeText: {},
      sideBar: {},
      gridApi: null,
      columnApi: null,
      frameworkComponents: null,
      overlayLoadingTemplate: null,
      num_hover: 8,
      excelStyles: null,
    }
  },
  computed: {
    CAN_EDIT() {
      return this.IS_LIST ? this.$store.state.permisos.can_edit : true
    },
    mergedDefaultColDef() {
      return {
        ...this.defaultColDef,
        valueGetter: (p) => {
          const f = p.colDef.field
          let v = p.data ? p.data[f] : undefined
          if (v === undefined && p.colDef.headerName) v = p.data?.[p.colDef.headerName]
          return v
        },
        valueFormatter: (params) => this.formatYesNo(params),
      }
    },
  },
  watch: {
    items: function (value) {
      if (this.gridApi) this.gridApi.hideOverlay()
    },
  },
  methods: {
    async autoSize() {
      if (this.columnApi) {
        const ids = this.columnApi.getAllColumns()?.map((c) => c.getColId()) || []
        if (ids.length) this.columnApi.autoSizeColumns(ids, false)
        this.gridApi.sizeColumnsToFit()
      }
    },
    checkFile(e) {
      //console.info(e)
      //console.info(this.file)
    },
    excelImport() {},
    excelExport() {
      // Get Headers keys
      let keys = []
      const headers = this.headers
        .filter((e) => e.field || e.children)
        .map((e) => e.field || e.children)
      for (let i = 0; i < headers.length; i++) {
        const element = headers[i]
        if (Array.isArray(element)) {
          keys = keys.concat(element.map((e) => e.field))
        } else {
          keys.push(element)
        }
      }
      const params = {
        allColumns: true,
        columnKeys: keys,
        processCellCallback: (params) => {
          const cellClass = params.column.colDef.cellClass
          const value = params.value
          return cellClass === 'date'
            ? this.$moment(params.value).format('DD-MM-YYYY')
            : params.value
        },
      }
      this.gridApi.exportDataAsExcel(params)
    },
    onGridReady(params) {
      this.gridApi = params.api
      // if (!this.hideOverlay) this.gridApi.showLoadingOverlay()
      this.columnApi = params.columnApi
      // 1) auto-size por contenido (incluye header)
      const allCols = this.columnApi.getAllColumns()?.map((c) => c.getColId()) || []
      if (allCols.length) this.columnApi.autoSizeColumns(allCols, false)
      // 2) luego ajusta para ocupar el ancho disponible
      this.gridApi.sizeColumnsToFit()
      this.gridApi.expandAll()
      this.$emit('gridApi', this.gridApi)
      this.$emit('columnApi', this.columnApi)

      // this.gridApi.forEachNode(node =>{
      //   node.expanded= true;
      // });
      // this.gridApi.onGroupExpandedOrCollapsed();
    },
    updateFilter() {
      this.gridApi.setQuickFilter(this.search)
    },
    clickRow(row) {
      if (!this.disableClickRow && !this.emitClickRow) {
        if (row.data) {
          if (this.CAN_EDIT) {
            const route = `${this.$nuxt.$route.path}/${row.data.uuid}`
            this.$router.push(route)
          } else {
            this.$store.commit('notification/show', {
              text: 'No tiene permisos suficientes.',
              color: 'error',
              timeout: 5000,
            })
          }
        }
      } else if (!this.disableClickRow && this.emitClickRow) {
        if (!row.data.index) row.data.index = row.rowIndex
        this.$emit('click-row', row.data)
      }
    },
    onSelectionChange() {
      const selection = this.gridApi.getSelectedRows()
      this.$emit('selectionChanged', selection)
    },
    getRowStyle(params) {
      if (params.node.rowIndex % 2 === 0) {
        return { background: '#f9f9f9' }
      }
    },
    formatCell(params) {
      //console.log('params', params);
      const value = params.value

      if (Array.isArray(value)) {
        if (value.length === 0) return ''
        // Caso array de objetos { razonSocial / nombre / texto }
        if (typeof value[0] === 'object') {
          return value
            .map((v) => v?.razonSocial || v?.nombre || v?.texto || v?.label || v?.value || '')
            .join(', ')
        }
        // Caso array de strings
        return value.join(', ')
      }

      if (value && typeof value === 'object') {
        return value.razonSocial || value.nombre || value.texto || value.label || value.value || ''
      }

      return value ?? ''
    },
    formatYesNo(p) {
      // Acepta params o valor crudo
      let val = p && typeof p === 'object' && 'value' in p ? p.value : p

      // si la columna no tiene field (checkbox/estado), no muestres nada
      if (p && p.colDef && !p.colDef.field) return ''

      if (Array.isArray(val)) {
        if (!val.length) return ''
        if (typeof val[0] === 'object') {
          return val
            .map((v) => v?.razonSocial || v?.nombre || v?.texto || v?.label || v?.val || '')
            .join(', ')
        }
        return val.join(', ')
      }

      if (val && typeof val === 'object') {
        return val.razonSocial || val.nombre || val.texto || val.label || val.value || ''
      }

      if (
        val === 1 ||
        val === '1' ||
        val === true ||
        val === 'true' ||
        val === 'Sí' ||
        val === 'si' ||
        val === 'SI'
      ) {
        return this.$t('generales.si')
      }
      if (
        val === 0 ||
        val === '0' ||
        val === false ||
        val === 'false' ||
        val === 'No' ||
        val === 'no' ||
        val === 'NO'
      ) {
        return this.$t('generales.no')
      }

      // en vez de '-', devuelve vacío si no hay valor
      if (val === null || val === undefined || val === '') return ''

      return val
    },
  },
}
</script>

<style>
.ag-header-cell,
.ag-header-group-cell {
  border-right: 0px solid #e2e2e2 !important;
  border-bottom: 4px solid grey !important;
}

.theme--light.v-data-table tbody tr:hover:not(.v-data-table__expand-row) {
  background: #eeeeee;
  cursor: pointer !important;
}

.ag-container {
  height: 100% !important;
  display: flex;
  flex-direction: column;
}

.ag-grid {
  height: 1px;
  min-height: 1px;
  flex-grow: 1;
  --ag-odd-row-background-color: #d8d8d8;
  --ag-even-row-background-color: #ececec;
}

.ag-grid .ag-row-odd {
  background-color: var(--ag-odd-row-background-color);
}

.ag-grid .ag-row-even {
  background-color: var(--ag-even-row-background-color);
}

.ag-cell {
  font-size: 1.1em !important;
}

.ag-header-cell-text {
  font-size: 1.3em !important;
  font-weight: bold;
}

.ag-theme-material .ag-side-bar .ag-side-buttons {
  width: 100% !important;
  padding: 0 !important;
}

.ag-theme-material .ag-side-bar .ag-side-buttons .ag-side-button button {
  min-height: 0 !important;
  padding: 0 0 16px 0 !important;
}
</style>
